import { themeVariables } from '@noths/polaris-design-system-abstract-theme';
import { css } from 'styled-components';

const { borderRadius, colors, spacing, typography } = themeVariables;

/**
 * The tight corners of the badge's asymmetric shape. Stated as a literal because
 * the design leaves this value untokenised too: in Figma the top-left and
 * bottom-right corners reference the same Border Radius/Base token the card
 * uses, while the top-right and bottom-left are a raw 2px.
 */
const TIGHT_CORNER_RADIUS = '2px';

/** How far the content behind a translucent badge is blurred. */
const BACKDROP_BLUR = '10px';

/**
 * The compact label's type, which the design calls Body XS.
 *
 * Stated here rather than read from a token because the theme's smallest body
 * step is `sm` at 13px, and this is 10px. Using `sm` would make the badge about a
 * third larger than designed, which defeats the point of the compact size: it
 * exists so the badge does not dominate the product image it sits on. Replace
 * both with `typography.body.sizes.xs` if that step is ever added.
 */
const COMPACT_FONT_SIZE = '10px';
const COMPACT_LINE_HEIGHT = '14px';

/* One static class for every tone and size. Each variant swaps CSS custom
   properties through an attribute selector rather than interpolating a prop, so
   styled-components generates and injects a single rule however many badges a
   listing renders. */
export const badge = css`
  /* Translucent, with the content behind it blurred, rather than an opaque chip:
     the badge sits over a product photo and should not blank it out. */
  --badge-surface: color-mix(in srgb, var(${colors.surface.default}) 80%, transparent);
  --badge-content: var(${colors.content.default});
  /* The icon carries its own colour rather than inheriting the label's. */
  --badge-icon: var(${colors.content.brand});
  --badge-font-size: var(${typography.body.sizes.md.fontSize});
  --badge-line-height: var(${typography.body.sizes.md.lineHeight});

  &[data-tone='urgent'] {
    --badge-surface: color-mix(in srgb, var(${colors.surface.negative}) 80%, transparent);
    --badge-icon: var(${colors.content.negative});
  }

  &[data-tone='sale'] {
    /* The theme's surface.negative is a 10% tint, so the solid red the design
       uses here is the negative *content* colour pressed into service as a fill.
       The design pairs it with a content-inverse token the abstract theme does
       not have; content.action resolves to the same near-white. */
    --badge-surface: var(${colors.content.negative});
    --badge-content: var(${colors.content.action});
    --badge-icon: var(--badge-content);
  }

  &[data-size='compact'] {
    --badge-font-size: ${COMPACT_FONT_SIZE};
    --badge-line-height: ${COMPACT_LINE_HEIGHT};
  }

  display: inline-flex;
  align-items: center;
  gap: var(${spacing.xxs});
  box-sizing: border-box;
  max-width: 100%;
  padding: var(${spacing.xxs}) var(${spacing.xs});
  /* Asymmetric on purpose: rounded at the top-left and bottom-right, tight at
     the other two. Order is top-left, top-right, bottom-right, bottom-left. */
  border-radius: var(${borderRadius.md.mobile}) ${TIGHT_CORNER_RADIUS}
    var(${borderRadius.md.mobile}) ${TIGHT_CORNER_RADIUS};
  background-color: var(--badge-surface);
  backdrop-filter: blur(${BACKDROP_BLUR});
  font-family: var(${typography.body.fontFamily});
  font-size: var(--badge-font-size);
  line-height: var(--badge-line-height);
  font-weight: 700;
  color: var(--badge-content);
`;

/* The label truncates rather than wrapping, so a long badge never turns a
   single-line box into a two-line block over the top of an image. */
export const label = css`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

/* The icon stays 16px at both sizes, as the design has it, so it does not shrink
   below the point where its detail reads. */
export const icon = css`
  display: inline-flex;
  flex-shrink: 0;
  line-height: 0;
  color: var(--badge-icon);
`;
